import { useEffect, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { startCase } from 'lodash-es'

import { useGetQRASavedStateQuery } from 'app/services/api/qra/savedstates'
import Toolbar, { Separator } from 'common/components/Toolbar'
import { IQRANavUtilsConsumerComponentInitProps } from 'common/utils/navigation/qraNavUtils'

import { IWisStateAssessment } from 'features/RiskManagement/Assessments'
import WISCreateModalQueryStringHandler from 'features/RiskManagement/Assessments/Scenarios/WISCreateModalQueryStringHandler'
import { useSavedStateAssessmentStatusEffect } from 'features/RiskManagement/Assessments/utils/useSavedStateAssessmentStatusEffect'
import { WISAssessmentStatusCode } from 'features/RiskManagement/Assessments/WISAssessmentStatusCode'
import LocalFilterButton from 'features/RiskManagement/Components/LocalFilter/LocalFilterButton'
import DisplayThresholdButtonAndModal from 'features/RiskManagement/Components/LocalFilter/RiskUnitAndThreshold'
import GraphsAndTableTab from 'features/RiskManagement/Components/Toolbar/GraphsAndTableTab'
import WISDropdownTab from 'features/RiskManagement/Components/Toolbar/WISDropdownTab'
// REVIEW: This is from QRA current state?
import CreateScenarioButtonWithPopup from 'features/RiskManagement/QRA/Components/Buttons/CreateScenarioButtonWithPopup'

interface ISavedStateToolbar extends ISavedStateWISDropdown {
  assessmentId: string | undefined
}

interface ISavedStateWISDropdown
  extends IQRANavUtilsConsumerComponentInitProps {
  stateId: string | undefined
  baselineId: string | undefined
}

const deriveStatusMessage = (selectedItem: IWisStateAssessment) => {
  if (
    selectedItem.status === WISAssessmentStatusCode.Error ||
    selectedItem.status === WISAssessmentStatusCode.InProcess
  ) {
    let selectedAssessmentStatus = ''

    if (selectedItem.totalSegmentCount) {
      selectedAssessmentStatus += ` (${selectedItem.counterState} of ${selectedItem.totalSegmentCount})`
    }

    if (selectedItem.remainingMinutesEstimation) {
      selectedAssessmentStatus += ` – ${selectedItem.remainingMinutesEstimation} mins remaining`
    }

    return ` ${startCase(selectedItem.status)}${selectedAssessmentStatus}`
  }
}

const SavedStateToolbar = ({
  qraBasePath,
  qraViewBasePath,
  stateId,
  baselineId,
  assessmentId
}: ISavedStateToolbar) => {
  useSavedStateAssessmentStatusEffect(stateId, assessmentId)

  return (
    <Toolbar>
      <GraphsAndTableTab
        qraBasePath={qraBasePath}
        qraViewBasePath={qraViewBasePath}
      />
      <Separator />
      <LocalFilterButton />
      <Separator />

      <WISCreateModalQueryStringHandler
        stateId={stateId}
        assessmentId={assessmentId}
      />
      <SavedStateWISDropdown
        qraBasePath={qraBasePath}
        qraViewBasePath={qraViewBasePath}
        stateId={stateId}
        baselineId={baselineId}
      />
      <div className='flex-1' />
      <DisplayThresholdButtonAndModal />
    </Toolbar>
  )
}

/**
 * REVIEW: The feature to auto select the assessment in the dropdown
 *         when navigating directly to WIS link was removed since #1461..
 */
export const SavedStateWISDropdown = ({
  qraBasePath,
  stateId,
  baselineId
}: ISavedStateWISDropdown) => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const { assessmentId } = useParams<{ assessmentId: string }>()

  const assessmentSelectedId = assessmentId ?? String(baselineId ?? '')

  const [assessmentItems, setAssessmentItems] = useState<IWisStateAssessment[]>(
    []
  )

  const { state, isLoading, isUninitialized } = useGetQRASavedStateQuery(
    stateId ?? skipToken,
    {
      selectFromResult: ({ currentData, isLoading, isUninitialized }) => ({
        state: currentData,
        isLoading,
        isUninitialized
      })
    }
  )

  useEffect(() => {
    const assessmentItems = [...(state?.assessments || [])]
      .sort((a, b) => {
        const dateA = new Date(a.dateCreated)
        const dateB = new Date(b.dateCreated)

        return dateA.getTime() - dateB.getTime()
      })
      .map((assessment) => {
        let newAssessmentId = assessment.id.toString()

        if (newAssessmentId === state!.baselineId) {
          newAssessmentId = `assessment-${newAssessmentId}`
        }

        const res = {
          id: newAssessmentId,
          title: assessment.name,
          totalSegmentCount: assessment.totalSegmentCount,
          counterState: assessment.counterState,
          remainingMinutesEstimation: assessment.remainingMinutesEstimation,
          status: startCase(assessment.statusCode) as string | undefined
        }

        return res
      })

    let newBaselineId = state?.baselineId

    // Additional check in case we decide to treat baseline as assessment

    const itemWithSameIdAsBaseline = assessmentItems.find((item) => {
      return item.id.split('-')[1] === state!.baselineId
    })

    if (itemWithSameIdAsBaseline) {
      newBaselineId = `state-${newBaselineId}`
    }

    const defaultSelectedBaseline = {
      id: newBaselineId!,
      title: 'Baseline',
      totalSegmentCount: 0,
      counterState: 0,
      remainingMinutesEstimation: 0,
      status: undefined
    }

    assessmentItems.unshift(defaultSelectedBaseline)

    setAssessmentItems(assessmentItems)
  }, [state])

  const scenarioSelectCB = (id: string): void => {
    if (id === assessmentSelectedId) {
      return
    }

    const selectedIdPrefix = id.toString().split('-')[0]
    let nextSavedStateIds = stateId ?? ''

    const hasSharedId = ['assessment', 'state'].includes(selectedIdPrefix)

    if (hasSharedId) {
      const assessmentIdSuffix = assessmentSelectedId?.split('-')[1]
      const selectedIdSuffix = id.split('-')[1]

      const isAnAssessmentWithSharedId = selectedIdPrefix === 'assessment'
      const isAnAssessmentWithNoSharedId = assessmentIdSuffix !== baselineId

      if (isAnAssessmentWithSharedId || isAnAssessmentWithNoSharedId) {
        nextSavedStateIds += `/assessments/${selectedIdSuffix}`
      }
    } else if (id !== baselineId) {
      nextSavedStateIds += `/assessments/${id}`
    }

    navigate(`${qraBasePath}/states/${nextSavedStateIds}${search}`)
  }

  const selectedItem = assessmentItems.find(
    (assessItem) => assessItem.id === assessmentSelectedId
  )

  return state?.assessments == null ? (
    <WISDropdownTab
      finalComponent={
        isUninitialized || isLoading ? <div>Loading..</div> : <div>No Data</div>
      }
    />
  ) : (
    <WISDropdownTab
      status={selectedItem && deriveStatusMessage(selectedItem)}
      items={assessmentItems}
      idSelected={assessmentSelectedId}
      idSelectCB={scenarioSelectCB}
      finalComponent={<CreateScenarioButtonWithPopup stateId={stateId} />}
    />
  )
}

export default SavedStateToolbar
